const REFUSED_KEY_PREFIX = 'fullstack:auto-gift:refused:';
const ABOVE_KEY_PREFIX = 'fullstack:auto-gift:above:';
const GIFT_IN_CART_KEY_PREFIX = 'fullstack:auto-gift:in-cart:';

const giftConfigs = new Map();

export function buildGiftLineProperties(blockId, giftBadge) {
  const properties = { _is_gift: 'true', _gift_block_id: blockId };

  if (typeof giftBadge === 'string' && giftBadge.trim() !== '') {
    properties._gift_badge = giftBadge.trim();
  }

  return properties;
}

export function registerGiftConfig(blockId, config, element) {
  giftConfigs.set(blockId, { ...config, element });
}

export function unregisterGiftConfig(blockId, element) {
  const entry = giftConfigs.get(blockId);
  if (entry?.element === element) {
    giftConfigs.delete(blockId);
  }
}

export function getRefused(blockId) {
  return sessionStorage.getItem(REFUSED_KEY_PREFIX + blockId) === 'true';
}

export function setRefused(blockId, value) {
  sessionStorage.setItem(REFUSED_KEY_PREFIX + blockId, String(value));
}

export function setAbove(blockId, value) {
  sessionStorage.setItem(ABOVE_KEY_PREFIX + blockId, String(value));
}

export function setGiftInCart(blockId, value) {
  sessionStorage.setItem(GIFT_IN_CART_KEY_PREFIX + blockId, String(value));
}

function getAbove(blockId) {
  return sessionStorage.getItem(ABOVE_KEY_PREFIX + blockId) === 'true';
}

function getGiftInCart(blockId) {
  return sessionStorage.getItem(GIFT_IN_CART_KEY_PREFIX + blockId) === 'true';
}

export function extractCartItemCollections(root) {
  const scripts = root.querySelectorAll('[data-ref="cart-item-collections"]');
  const merged = {};

  for (const script of scripts) {
    try {
      Object.assign(merged, JSON.parse(script.textContent));
    } catch (error) {
      console.error('AutoGiftProcessor: invalid cart-item-collections JSON', error);
    }
  }

  return merged;
}

function evaluateThreshold(cart, config, cartItemCollections) {
  if (config.triggerType === 'cart_value') {
    let total = 0;

    for (const item of cart.items) {
      if (item.properties?._is_gift === 'true') continue;
      total += item.final_line_price;
    }

    return total >= Math.round(config.threshold * 100);
  }

  let quantity = 0;

  for (const item of cart.items) {
    if (item.properties?._is_gift === 'true') continue;

    if (config.collectionHandle) {
      const handles = cartItemCollections[item.product_id] || [];
      if (!handles.includes(config.collectionHandle)) continue;
    }

    quantity += item.quantity;
  }

  return quantity >= config.threshold;
}

function findGiftLines(cart, blockId) {
  return cart.items.filter((item) => item.properties?._is_gift === 'true' && item.properties?._gift_block_id === blockId);
}

function findUnauthorizedGifts(cart) {
  const giftOnly = window.__giftOnlyProducts || {};
  return cart.items.filter((item) => {
    const isGiftOnly = giftOnly[item.product_id];
    const isMarkedAsGift = item.properties?._is_gift === 'true';
    return isGiftOnly && !isMarkedAsGift;
  });
}

export function reconcileState(blockId, above, giftInCart) {
  const previousAbove = getAbove(blockId);
  const previousGiftInCart = getGiftInCart(blockId);

  if (previousAbove && !above) {
    setRefused(blockId, false);
  }

  if (previousGiftInCart && !giftInCart && above) {
    setRefused(blockId, true);
  }

  setAbove(blockId, above);
  setGiftInCart(blockId, giftInCart);
}

function planOperations(cart, cartItemCollections) {
  const removes = [];
  const adds = [];
  const popups = [];

  for (const [blockId, config] of giftConfigs) {
    const giftLines = findGiftLines(cart, blockId);
    const giftInCart = giftLines.length > 0;
    const above = evaluateThreshold(cart, config, cartItemCollections);

    reconcileState(blockId, above, giftInCart);

    const refused = getRefused(blockId);

    if (above && !giftInCart && !refused) {
      if (config.needsVariantChoice) {
        popups.push(blockId);
        continue;
      }

      const items = config.products.map((product) => ({
        id: product.defaultVariantId,
        quantity: config.quantityPerProduct,
        properties: buildGiftLineProperties(blockId, config.giftBadge),
      }));
      adds.push({ blockId, items });
    } else if (!above && giftInCart) {
      removes.push({ blockId, lines: giftLines });
    }
  }

  const unauthorizedLines = findUnauthorizedGifts(cart);

  return { removes, adds, popups, unauthorizedLines };
}

async function executeOperations(operations, sectionIds) {
  const sections = sectionIds.join(',');
  let lastData = null;

  for (const { blockId, lines } of operations.removes) {
    for (const line of lines) {
      try {
        const response = await fetch(Theme.routes.cart_change_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: line.key, quantity: 0, sections }),
        });
        lastData = await response.json();
      } catch (error) {
        console.error('AutoGiftProcessor: failed to remove gift line', error);
      }
    }

    setGiftInCart(blockId, false);
  }

  for (const line of operations.unauthorizedLines) {
    try {
      const response = await fetch(Theme.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: line.key, quantity: 0, sections }),
      });
      lastData = await response.json();
    } catch (error) {
      console.error('AutoGiftProcessor: failed to remove unauthorized gift purchase', error);
    }
  }

  for (const { blockId, items } of operations.adds) {
    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections }),
      });
      const data = await response.json();

      if (data.status >= 400 || data.errors) {
        console.warn('AutoGiftProcessor: failed to add gift items', data);
        continue;
      }

      lastData = data;
      setGiftInCart(blockId, true);
    } catch (error) {
      console.error('AutoGiftProcessor: failed to add gift items', error);
    }
  }

  return lastData;
}

export async function processGifts(sectionId, cartItemCollections = null) {
  if (giftConfigs.size === 0) return null;

  let cart;

  try {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return null;
    cart = await response.json();
  } catch {
    return null;
  }

  const hasCollections = cartItemCollections && Object.keys(cartItemCollections).length > 0;
  const collections = hasCollections ? cartItemCollections : extractCartItemCollections(document);
  const operations = planOperations(cart, collections);

  for (const blockId of operations.popups) {
    const entry = giftConfigs.get(blockId);
    entry?.element?.openPopupIfNeeded();
  }

  if (operations.removes.length === 0 && operations.adds.length === 0 && operations.unauthorizedLines.length === 0) return null;

  const sections = ['auto-gifts'];
  if (sectionId) sections.push(sectionId);
  const lastData = await executeOperations(operations, sections);

  if (!lastData) return null;

  return {
    sections: lastData.sections,
    itemCount: lastData.item_count,
  };
}
